import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, inject, Input, ViewChild, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { FieldComponent } from '../field/field.component'

/**
 * @public
 */
export type TextareaElement = NgElement & WithProperties<TextareaComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: [ './textarea.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [ CommonModule ]
})
export class TextareaComponent extends FieldComponent implements AfterViewInit {

  static readonly DEFAULT_MAX_LENGTH = 300

  @ViewChild('textarea')
  private readonly _textareaRef: ElementRef<HTMLTextAreaElement>

  protected _elementRef = inject(ElementRef)

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('maxlength')
  @HostBinding()
  maxLength = TextareaComponent.DEFAULT_MAX_LENGTH

  ngAfterViewInit(): void {
    this.syncValidity(this._textareaRef?.nativeElement)
  }

  onInput(event: Event): void {
    // Pause propagation of input event
    event.stopPropagation()
    let target = (event as InputEvent).target as HTMLTextAreaElement

    // Set the value
    this.writeValue(target.value)

    // Mark it as touched
    this.touched = true

    // Update the validity of the element
    this.syncValidity(target)

    // Detect changes
    this._changeDetectorRef.detectChanges()

    // Resume propagation of input event
    this._elementRef.nativeElement.dispatchEvent(new InputEvent('input', { ...event, bubbles: true }))
  }

  /**
   * Synchronize the validity of the element with the validity state of the provided element
   *
   * @param element The target element
   */
  private syncValidity(element: HTMLTextAreaElement): void {
    this.setValidity(
      element?.validity,
      element?.validationMessage,
      element
    )
  }

  @Input()
  @HostBinding()
  set value(value: string) {
    super.value = value
    this._changeDetectorRef.detectChanges()
    this.syncValidity(this._textareaRef?.nativeElement)
  }

  get value(): string {
    return super.value
  }

}

/**
 * @public
 */
export const registerTextareaElement = registerCustomElementFactory(TextareaComponent)
